(() => {
  const instances = new WeakMap();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  let active, serial = 0;
  class AnimatedSelect {
    constructor(select) {
      this.select = select;
      this.wrapper = document.createElement('div');
      this.wrapper.className = 'animated-select';
      this.pageDropdown = !select.closest('dialog, [role="dialog"], .drawer, .menu-drawer, .mobile-facets, .quick-add-modal, .search-modal__content');
      this.wrapper.classList.toggle('animated-select--page', this.pageDropdown);
      select.before(this.wrapper);
      this.wrapper.append(select);
      select.tabIndex = -1;
      select.setAttribute('aria-hidden', 'true');
      this.trigger = document.createElement('button');
      this.trigger.type = 'button';
      this.trigger.className = 'animated-select__trigger';
      this.trigger.id = `AnimatedSelect-${++serial}`;
      this.trigger.setAttribute('role', 'combobox');
      this.trigger.setAttribute('aria-haspopup', 'listbox');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.setAttribute('aria-label', select.getAttribute('aria-label') || [...select.labels].map(label => label.textContent.trim()).join(' ') || select.name || 'Select an option');
      this.list = document.createElement('div');
      this.list.id = `${this.trigger.id}-options`;
      this.list.className = 'animated-select__list';
      this.list.setAttribute('role', 'listbox');
      this.list.setAttribute('aria-labelledby', this.trigger.id);
      this.list.hidden = true;
      this.trigger.setAttribute('aria-controls', this.list.id);
      this.wrapper.append(this.trigger, this.list);
      this.sync();
      this.trigger.addEventListener('click', () => this.open ? this.close() : this.show());
      this.wrapper.addEventListener('keydown', event => this.onKey(event));
      select.addEventListener('change', () => this.sync());
      select.addEventListener('invalid', event => { event.preventDefault(); this.trigger.focus(); });
      select.form?.addEventListener('reset', () => setTimeout(() => this.sync()));
      new MutationObserver(() => this.sync()).observe(select, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'selected', 'label', 'value'] });
      instances.set(select, this);
    }
    sync() {
      this.trigger.textContent = this.select.selectedOptions[0]?.textContent || '';
      this.trigger.disabled = this.select.disabled;
      this.trigger.setAttribute('aria-required', String(this.select.required));
      this.list.replaceChildren();
      [...this.select.options].forEach((option, index) => {
        if (option.hidden) return;
        const row = document.createElement('button');
        row.type = 'button'; row.className = 'animated-select__option'; row.tabIndex = -1;
        row.setAttribute('role', 'option'); row.setAttribute('aria-selected', String(option.selected));
        row.disabled = option.disabled || !!option.closest('optgroup')?.disabled;
        row.textContent = option.label;
        row.dataset.index = index;
        row.addEventListener('click', () => this.choose(index));
        this.list.append(row);
      });
    }
    animate(opening) {
      this.animation?.cancel();
      if (reduced.matches) { if (!opening) this.list.hidden = true; return; }
      this.animation = this.list.animate(opening ? [{ opacity: 0, transform: 'translateY(-5px)' }, { opacity: 1, transform: 'translateY(0)' }] : [{ opacity: 1, transform: 'translateY(0)' }, { opacity: 0, transform: 'translateY(-5px)' }], { duration: 180, easing: 'ease-out' });
      if (!opening) this.animation.onfinish = () => { this.list.hidden = true; this.wrapper.classList.remove('is-open'); };
    }
    show() {
      if (this.select.disabled) return;
      active?.close(); active = this; this.open = true;
      this.list.hidden = false;
      this.wrapper.classList.add('is-open');
      const bounds = this.wrapper.getBoundingClientRect();
      const menuHeight = Math.min(parseFloat(getComputedStyle(this.list).maxHeight) || Infinity, this.list.scrollHeight) + 6;
      this.wrapper.classList.toggle('opens-up', !this.pageDropdown && innerHeight - bounds.bottom < menuHeight && bounds.top > menuHeight);
      this.trigger.setAttribute('aria-expanded', 'true');
      this.animate(true);
    }
    close(focus = false) {
      if (!this.open) return;
      this.open = false; if (active === this) active = null;
      this.trigger.setAttribute('aria-expanded', 'false');
      this.animate(false);
      if (reduced.matches) this.wrapper.classList.remove('is-open');
      if (focus) this.trigger.focus();
    }
    choose(index) {
      const changed = this.select.selectedIndex !== index;
      this.select.selectedIndex = index;
      this.sync(); this.close(true);
      if (changed) {
        this.select.dispatchEvent(new Event('input', { bubbles: true }));
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    onKey(event) {
      const rows = [...this.list.querySelectorAll('button:not(:disabled)')];
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); this.close(true); return; }
      if (event.key === 'Tab') { this.close(); return; }
      if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
        event.preventDefault(); if (!this.open) this.show();
        let index = rows.indexOf(document.activeElement);
        if (index < 0) index = rows.findIndex(row => row.getAttribute('aria-selected') === 'true');
        index = event.key === 'Home' ? 0 : event.key === 'End' ? rows.length - 1 : Math.max(0, Math.min(rows.length - 1, index + (event.key === 'ArrowUp' ? -1 : 1)));
        rows[index]?.focus();
      } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && event.key !== ' ') {
        if (!this.open) this.show();
        const row = rows.find(row => row.textContent.toLowerCase().startsWith(event.key.toLowerCase()));
        if (row) { event.preventDefault(); row.focus(); }
      }
    }
  }
  function enhance(root) {
    const selects = root.matches?.('select') ? [root] : root.querySelectorAll?.('select') || [];
    for (const select of selects) {
      if (instances.has(select) || select.multiple || select.size > 1 || select.hidden || select.closest('.animated-select, .visually-hidden, [data-native-select]')) continue;
      new AnimatedSelect(select);
    }
  }
  enhance(document);
  new MutationObserver(records => records.forEach(record => record.addedNodes.forEach(node => { if (node.nodeType === 1) enhance(node); }))).observe(document.body, { childList: true, subtree: true });
  document.addEventListener('click', event => {
    if (active && !active.wrapper.contains(event.target)) active.close();
    const label = event.target.closest('label[for]');
    const instance = label && instances.get(document.getElementById(label.htmlFor));
    if (instance) { event.preventDefault(); instance.trigger.focus(); }
  });
})();
